/**
 * Workshop & Service Pool — every KUKA component not currently fitted to a
 * system, INCLUDING the parts still sitting inside them (a workshop KRC
 * keeps its KPP until someone records its removal here). Move works from
 * this page: pull a part out of a pooled parent, refit a repaired part to
 * a system, or scrap it. The live replacement for the serial sheet's
 * "Needs Repaired" block.
 */

import React, { useState, useEffect, useCallback } from 'react';
import BackToList from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import { useComponentTypes, ComponentHistoryList, MoveModal } from '../components/AssetComponentsPanel';
import '../components/AssetComponentsPanel.css';
import './Assets.css';

const POOL_STATUSES = [
  { value: 'workshop', label: 'Workshop' },
  { value: 'service_pool', label: 'Service pool' },
  { value: 'out_for_repair', label: 'Out for repair' },
  { value: 'scrapped', label: 'Scrapped' }
];
const READ_ONLY_ROLES = ['commercial', 'operations_coordinator'];

function ComponentPool() {
  const { authFetch, user } = useAuth();
  const { labelFor, kindOf } = useComponentTypes();
  const [status, setStatus] = useState('workshop');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [historyFor, setHistoryFor] = useState(null);
  const [moving, setMoving] = useState(null);

  const canMove = !READ_ONLY_ROLES.includes(user?.role);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, include_children: 'true' });
      if (searchTerm.trim()) params.set('q', searchTerm.trim());
      const response = await authFetch(`/api/v1/components?${params}`);
      if (response.ok) setRows((await response.json()).data || []);
    } catch (err) {
      console.error('Failed to fetch component pool:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, status, searchTerm]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const poolItems = rows.filter(row => row.status === status);
  const childrenOf = (id) => rows.filter(row => row.parent_component_id === id);

  const renderRow = (component, isChild) => (
    <div key={component.component_id} className={`component-row${isChild ? ' component-row-child' : ''}`}>
      <span className="component-type">{labelFor(component.component_type)}</span>
      <span className="component-serial">{component.serial_number}</span>
      {isChild && <span className="component-pool-note">still fitted inside</span>}
      {!isChild && component.notes && <span className="component-pool-note">{component.notes}</span>}
      <span className="component-actions">
        {canMove && (
          <button className="btn-small" onClick={() => setMoving(component)}>Move</button>
        )}
        <button
          className="btn-small"
          onClick={() => setHistoryFor(historyFor === component.component_id ? null : component.component_id)}
        >
          History
        </button>
      </span>
      {historyFor === component.component_id && (
        <div className="component-history-holder">
          <ComponentHistoryList componentId={component.component_id} authFetch={authFetch} labelFor={labelFor} />
        </div>
      )}
    </div>
  );

  return (
    <div className="assets-page">
      <div className="page-header">
        <div>
          <h1>Workshop &amp; Service Pool</h1>
          <p>
            Components not fitted to a system, with any parts still inside them. Use Move to pull a
            part out, refit it to a system, or scrap it.
          </p>
        </div>
        <BackToList to="/assets" className="btn-secondary">← Assets</BackToList>
      </div>

      <div className="assets-filters">
        <form onSubmit={e => { e.preventDefault(); setSearchTerm(searchInput); }} className="search-form">
          <input
            type="text"
            placeholder="Search by serial number..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
        </form>
        <select value={status} onChange={e => setStatus(e.target.value)} className="filter-select">
          {POOL_STATUSES.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : poolItems.length === 0 ? (
        <div className="components-empty">
          Nothing in {POOL_STATUSES.find(option => option.value === status)?.label.toLowerCase() || status.replace(/_/g, ' ')}.
        </div>
      ) : (
        poolItems.map(component => (
          <div key={component.component_id} className="component-group">
            {renderRow(component, false)}
            {childrenOf(component.component_id).map(child => renderRow(child, true))}
          </div>
        ))
      )}

      {moving && (
        <MoveModal
          component={moving}
          kind={kindOf(moving.component_type)}
          labelFor={labelFor}
          authFetch={authFetch}
          onClose={() => setMoving(null)}
          onMoved={() => { setMoving(null); fetchRows(); }}
        />
      )}
    </div>
  );
}

export default ComponentPool;
